import React, { useCallback, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Button, Card, Dot, Input, SectionHeader, Skeleton } from '../../../components/ui';
import type { MonitoringThresholds as Thresholds } from '../../../types/monitoring';
import { DEFAULT_THRESHOLDS } from '../../../types/monitoring';
import { isAdminRole } from '../../intelligence/components/BriefPanel';
import { fetchThresholds, monitoringErrorMessage, MonitoringUnavailableError, saveThresholds } from '../monitoringApi';
import { Notice } from './ui';

type Key = keyof Thresholds;

interface Setting {
  key: Key;
  label: string;
  /** One short line: what this number does */
  hint: string;
  min: number;
  max: number;
  step: number;
  integer?: boolean;
  suffix?: string;
}

const SETTINGS: Setting[] = [
  {
    key: 'sentimentDrop',
    label: 'Sentiment drop',
    hint: 'How far mean sentiment must fall before an alert is raised.',
    min: 0.05,
    max: 2,
    step: 0.05,
  },
  {
    key: 'volumeSpikeFactor',
    label: 'Spike factor',
    hint: 'How many times the usual coverage counts as a surge.',
    min: 1.1,
    max: 10,
    step: 0.1,
    suffix: '×',
  },
  {
    key: 'minSignals',
    label: 'Minimum signals',
    hint: 'Signals needed in the window before any rule may fire.',
    min: 1,
    max: 50,
    step: 1,
    integer: true,
  },
  {
    key: 'goingDarkDays',
    label: 'Going dark',
    hint: 'Days of silence before an influential stakeholder is flagged.',
    min: 7,
    max: 365,
    step: 1,
    integer: true,
    suffix: 'days',
  },
  {
    key: 'maxScansPerRun',
    label: 'Scans per run',
    hint: 'Ceiling on entities scanned per scheduled run, to bound AI cost.',
    min: 1,
    max: 200,
    step: 1,
    integer: true,
  },
];

/** The one-line reason a value is refused, or null when it is fine */
export function validateThreshold(setting: Setting, raw: string): string | null {
  if (raw.trim() === '') return 'Required';
  const value = Number(raw);
  if (Number.isNaN(value)) return 'Must be a number';
  if (setting.integer && !Number.isInteger(value)) return 'Whole numbers only';
  if (value < setting.min || value > setting.max) return `Between ${setting.min} and ${setting.max}`;
  return null;
}

const toForm = (values: Thresholds): Record<Key, string> =>
  SETTINGS.reduce((form, setting) => ({ ...form, [setting.key]: String(values[setting.key]) }), {} as Record<Key, string>);

/**
 * The numbers the rules engine uses. Admin-only, small enough to live inside the
 * AI settings page rather than a page of its own.
 */
export const MonitoringThresholdsSection: React.FC<{ className?: string }> = ({ className = '' }) => {
  const admin = isAdminRole();
  const [form, setForm] = useState<Record<Key, string>>(toForm(DEFAULT_THRESHOLDS));
  const [saved, setSaved] = useState<Thresholds | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!admin) return;
    let cancelled = false;
    fetchThresholds()
      .then((values) => {
        if (cancelled) return;
        setSaved(values);
        setForm(toForm(values));
      })
      .catch((err) => {
        if (cancelled) return;
        setUnavailable(err instanceof MonitoringUnavailableError);
        setError(monitoringErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [admin]);

  const errors = SETTINGS.reduce<Partial<Record<Key, string>>>((acc, setting) => {
    const message = validateThreshold(setting, form[setting.key]);
    if (message) acc[setting.key] = message;
    return acc;
  }, {});

  const dirty = !!saved && SETTINGS.some((setting) => Number(form[setting.key]) !== saved[setting.key]);
  const invalid = Object.keys(errors).length > 0;

  const submit = useCallback(async () => {
    if (invalid) return;
    setSaving(true);
    setError(null);
    try {
      const payload = SETTINGS.reduce(
        (values, setting) => ({ ...values, [setting.key]: Number(form[setting.key]) }),
        {} as Thresholds,
      );
      const next = await saveThresholds(payload);
      setSaved(next);
      setForm(toForm(next));
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 2400);
    } catch (err) {
      setError(monitoringErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [form, invalid]);

  // These numbers change what the whole organisation is alerted about
  if (!admin) return null;

  return (
    <section className={className}>
      <SectionHeader
        title="Monitoring thresholds"
        aside={
          dirty ? (
            <span className="inline-flex items-center gap-1.5">
              <Dot tone="red" /> Unsaved
            </span>
          ) : justSaved ? (
            <span className="inline-flex animate-fade-up items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> Saved
            </span>
          ) : undefined
        }
      />

      {unavailable ? (
        <Card className="p-3">
          <Notice tone="quiet">Monitoring is not available on this server yet — redeploy the backend to configure it.</Notice>
        </Card>
      ) : (
        <>
          <Card divided>
            {loading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-center justify-between gap-4 px-4 py-3">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-8 w-24" />
                  </div>
                ))
              : SETTINGS.map((setting) => (
                  <div key={setting.key} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                    <label htmlFor={`threshold-${setting.key}`} className="min-w-0 flex-1 cursor-pointer">
                      <span className="block text-[13px] text-ink">{setting.label}</span>
                      <span className="mt-0.5 block text-[12px] text-neutral-400">{setting.hint}</span>
                    </label>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <Input
                        id={`threshold-${setting.key}`}
                        type="number"
                        inputMode="decimal"
                        className="w-24 text-right tabular-nums"
                        min={setting.min}
                        max={setting.max}
                        step={setting.step}
                        value={form[setting.key]}
                        aria-invalid={!!errors[setting.key]}
                        aria-describedby={errors[setting.key] ? `threshold-${setting.key}-error` : undefined}
                        onChange={(event) => setForm((current) => ({ ...current, [setting.key]: event.target.value }))}
                      />
                      {setting.suffix && <span className="w-8 text-[12px] text-neutral-400">{setting.suffix}</span>}
                    </div>
                    {errors[setting.key] && (
                      <p id={`threshold-${setting.key}-error`} className="w-full text-[12px] text-primary-700">
                        {errors[setting.key]}
                      </p>
                    )}
                  </div>
                ))}
          </Card>

          {error && (
            <Notice className="mt-3" tone="error">
              {error}
            </Notice>
          )}

          <div className="mt-3 flex justify-end">
            <Button variant="primary" loading={saving} disabled={!dirty || invalid || loading} onClick={submit}>
              Save
            </Button>
          </div>
        </>
      )}
    </section>
  );
};
